import React from 'react';

import { fg } from '@atlaskit/platform-feature-flags/fg';
import { logException } from '@atlassian/conversation-assistant-instrumentation/services/sentry';
import { CreditsProvider, useReportCreditDenial } from '@atlassian/rovo-credits/credits-provider';
import type {
	CreditsErrorHandler,
	CreditsProduct,
	ReportedCreditDenial,
} from '@atlassian/rovo-credits/credits-provider/types';

import type { ConversationAssistantConfig } from './types';

const getCreditsProduct = (product: ConversationAssistantConfig['product']): CreditsProduct => {
	switch (product) {
		case 'confluence':
		case 'jsm':
		case 'rovo':
		case 'bitbucket':
		case 'trello':
		case 'home':
		case 'projects':
		case 'goals':
			return product;

		case 'jira':
		case 'jira-core':
		case 'jira-software':
		case 'jpd':
			return 'jira';

		default:
			return 'rovo';
	}
};

const reportCreditsError: CreditsErrorHandler = (error, context) => {
	void logException({
		exception: error,
		name: 'RovoCreditsProviderError',
		tags: {
			component: 'ConversationAssistantCreditsProvider',
			context,
		},
	});
};

type CreditsChildren =
	| React.ReactNode
	| ((reportCreditDenial?: (denial: ReportedCreditDenial) => void) => React.ReactNode);

/** Provides credits state for visible chat and creation surfaces. */
export const ConversationAssistantCreditsProvider = ({
	children,
	product,
	cloudId,
	disabled = false,
}: {
	children: CreditsChildren;
	product: ConversationAssistantConfig['product'];
	cloudId?: string;
	disabled?: boolean;
}): JSX.Element => {
	if (disabled || !fg('rovo_chat_credits')) {
		return <>{typeof children === 'function' ? children() : children}</>;
	}

	return (
		<CreditsProvider
			product={getCreditsProduct(product)}
			cloudId={cloudId}
			onError={reportCreditsError}
		>
			{typeof children === 'function' ? (
				<ConversationAssistantCreditsReporter>{children}</ConversationAssistantCreditsReporter>
			) : (
				children
			)}
		</CreditsProvider>
	);
};

const ConversationAssistantCreditsReporter = ({
	children,
}: {
	children: (reportCreditDenial: (denial: ReportedCreditDenial) => void) => React.ReactNode;
}): JSX.Element => {
	const reportCreditDenial = useReportCreditDenial();

	return <>{children(reportCreditDenial)}</>;
};
